<template>
  <div class="table-export-buttons d-flex gap-2 justify-content-end mb-2">
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="!hasData"
      @click="copyToClipboard"
    >
      <i class="fa fa-copy me-1"></i> Copy
    </button>
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="!hasData"
      @click="exportCSV"
    >
      <i class="fa fa-file-csv me-1"></i> CSV
    </button>
    <button
      type="button"
      class="btn btn-sm btn-outline-success"
      :disabled="!hasData"
      @click="exportExcel"
    >
      <i class="fa fa-file-excel me-1"></i> Excel
    </button>
    <button
      type="button"
      class="btn btn-sm btn-outline-danger"
      :disabled="!hasData"
      @click="exportPDF"
    >
      <i class="fa fa-file-pdf me-1"></i> PDF
    </button>
    <button
      type="button"
      class="btn btn-sm btn-outline-primary"
      :disabled="!hasData"
      @click="printTable"
    >
      <i class="fa fa-print me-1"></i> Print
    </button>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "TableExportButtons",
  props: {
    columns: {
      type: Array,
      required: true,
      default: () => [],
    },
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    fileName: {
      type: String,
      default: "export-data",
    },
    title: {
      type: String,
      default: "Report",
    },
  },
  computed: {
    hasData() {
      return Array.isArray(this.data) && this.data.length > 0;
    },
    headers() {
      return this.columns.map((col) => col.label || col.key);
    },
    formattedRows() {
      return this.data.map((row) => {
        return this.columns.map((col) => {
          let value = row[col.key];
          if (typeof col.formatter === "function") {
            value = col.formatter(value, row);
          }
          return value ?? "-";
        });
      });
    },
  },
  methods: {
    // 1. COPY TO CLIPBOARD
    async copyToClipboard() {
      const tsvContent = [
        this.headers.join("\t"),
        ...this.formattedRows.map((row) => row.join("\t")),
      ].join("\n");

      try {
        await navigator.clipboard.writeText(tsvContent);
        alert("Table data copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy data: ", err);
      }
    },

    // 2. EXPORT TO CSV
    exportCSV() {
      const csvRows = [
        this.headers.map((h) => `"${h}"`).join(","),
        ...this.formattedRows.map((row) =>
          row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")
        ),
      ];

      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${this.fileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // 3. EXPORT TO EXCEL
    exportExcel() {
      const objectRows = this.formattedRows.map((row) => {
        const obj = {};
        this.headers.forEach((header, index) => {
          obj[header] = row[index];
        });
        return obj;
      });

      const worksheet = XLSX.utils.json_to_sheet(objectRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(workbook, `${this.fileName}.xlsx`);
    },

    // 4. EXPORT TO PDF (FIXED)
    exportPDF() {
      const doc = new jsPDF({ orientation: "landscape" });

      doc.setFontSize(14);
      doc.text(this.title, 14, 15);

      // Call autoTable as a function passing 'doc'
      autoTable(doc, {
        head: [this.headers],
        body: this.formattedRows,
        startY: 20,
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
      });

      doc.save(`${this.fileName}.pdf`);
    },

    // 5. PRINT (TABLE ONLY FIX)
    printTable() {
      const printWindow = window.open("", "_blank");

      const tableHeaders = this.headers.map((h) => `<th>${h}</th>`).join("");
      const tableRows = this.formattedRows
        .map(
          (row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`
        )
        .join("");

      const printHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${this.title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
              h2 { font-size: 18px; margin-bottom: 15px; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; }
              th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; font-size: 12px; }
              th { background-color: #f2f2f2; font-weight: bold; }
              tr:nth-child(even) { background-color: #f9f9f9; }
            </style>
          </head>
          <body>
            <h2>${this.title}</h2>
            <table>
              <thead><tr>${tableHeaders}</tr></thead>
              <tbody>${tableRows}</tbody>
            </table>
          </body>
        </html>
      `;

      printWindow.document.write(printHtml);
      printWindow.document.close();
      printWindow.focus();

      // Trigger print after rendering completes
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },
  },
};
</script>
